/**
 * Consultants Management API endpoints
 */
import { api } from './baseApi';
import type { ProviderListFilters } from '../../types/providerProfile';
import type { Consultant } from '../../types/user';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface ConsultantResponse {
  success: boolean;
  data: Consultant;
}

export const consultantsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getConsultants: builder.query<PaginatedResponse<Consultant>, { search?: string; page?: number; page_size?: number } & ProviderListFilters>({
      query: (params) => ({
        url: '/dashboard/consultants/',
        params,
      }),
      providesTags: ['Consultants'],
    }),
    getConsultant: builder.query<ConsultantResponse, number>({
      query: (id) => `/dashboard/consultants/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Consultant', id }],
    }),
    getConsultantAvailabilitySlots: builder.query<unknown, number>({
      query: (consultantId) => `/dashboard/consultants/${consultantId}/availability-slots/`,
      providesTags: (_result, _error, id) => [{ type: 'Consultant', id }],
    }),
    createConsultant: builder.mutation<ConsultantResponse, FormData>({
      query: (formData) => ({
        url: '/dashboard/consultants/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Consultants'],
    }),
    updateConsultant: builder.mutation<ConsultantResponse, { id: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/dashboard/consultants/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Consultant', id }, 'Consultants'],
    }),
    deleteConsultant: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/consultants/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Consultants'],
    }),
    activateConsultant: builder.mutation<ConsultantResponse, number>({
      query: (id) => ({
        url: `/dashboard/consultants/${id}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Consultant', id }, 'Consultants'],
    }),
    deactivateConsultant: builder.mutation<ConsultantResponse, number>({
      query: (id) => ({
        url: `/dashboard/consultants/${id}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Consultant', id }, 'Consultants'],
    }),
  }),
});

export const {
  useGetConsultantsQuery,
  useGetConsultantQuery,
  useGetConsultantAvailabilitySlotsQuery,
  useCreateConsultantMutation,
  useUpdateConsultantMutation,
  useDeleteConsultantMutation,
  useActivateConsultantMutation,
  useDeactivateConsultantMutation,
} = consultantsApi;

